import { Controller } from 'stimulus';

/**
 * Always-on Yes/No pill switch for the two per-device charge exemptions —
 * "Charge for install?" and "Charge for month?" — in the device breakdown
 * modal. Toggling submits inline (no reload): a fetch PATCH whose turbo_stream
 * response re-renders the device count and the line items in place, so the
 * modal stays open and the page stays consistent. On failure the switch reverts
 * to its previous position.
 *
 * The cell lives inside a Tabulator table, so this controller owns its own
 * cell rather than letting the response replace it: Tabulator renders the
 * visible cells from row data extracted once on connect, and the id-bearing
 * source `<td>` is hidden from then on. On success we therefore (a) update the
 * switch and label in place and (b) write the updated cell HTML back into
 * Tabulator's row data, so a later redraw (sort, filter, page change) doesn't
 * restore the pre-toggle state.
 */
export default class extends Controller {
  static targets = ['switch', 'label'];
  static values = {
    url: String,
    sheetId: Number,
    deviceId: Number,
    chargeLabel: String,
    exemptLabel: String,
  };

  async toggle() {
    // Switch ON = "charge? Yes" (not exempt). OFF = exempt.
    const charge = this.switchTarget.checked;
    this.switchTarget.disabled = true;

    const body = new URLSearchParams();
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
    if (csrf) body.append('authenticity_token', csrf);
    body.append('sheet_id', this.sheetIdValue);
    body.append('device_id', this.deviceIdValue);
    body.append('exempt', !charge);

    try {
      const response = await fetch(this.urlValue, {
        method: 'PATCH',
        headers: {
          Accept: 'text/vnd.turbo-stream.html',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body,
      });

      if (!response.ok) throw new Error(`Toggle failed (${response.status})`);

      // Patches the device count and the line items — everything outside this
      // table. The cell itself is ours to update.
      window.Turbo.renderStreamMessage(await response.text());
      // `disabled` is a reflected attribute, so it must be cleared before
      // `applyState` copies the cell HTML into Tabulator's row data — otherwise
      // a rebuilt row renders a permanently disabled switch.
      this.switchTarget.disabled = false;
      this.applyState(charge);
    } catch (error) {
      // Revert the optimistic toggle and surface the failure.
      this.switchTarget.checked = !this.switchTarget.checked;
      if (window.sendError)
        window.sendError('Device charge toggle failed', error);
    } finally {
      this.switchTarget.disabled = false;
    }
  }

  applyState(charge) {
    this.labelTarget.textContent = charge
      ? this.chargeLabelValue
      : this.exemptLabelValue;
    // `checked` is a live property that doesn't serialise into innerHTML, so
    // move the attribute too before the cell is copied back into row data.
    this.switchTarget.toggleAttribute('checked', charge);
    this.syncTabulatorRowData();
  }

  // Keep Tabulator's stored HTML for this cell in step with the DOM. No-op for
  // the hidden source table's copy of the cell (no Tabulator ancestor).
  syncTabulatorRowData() {
    const cellEl = this.element.closest('.tabulator-cell');
    const field = cellEl?.getAttribute('tabulator-field');
    const rowEl = cellEl?.closest('.tabulator-row');
    const tableEl = this.element.closest(
      '[data-controller~="tabulator-table"]'
    );
    if (!field || !rowEl || !tableEl) return;

    const tabulator = this.application.getControllerForElementAndIdentifier(
      tableEl,
      'tabulator-table'
    )?.tabulator;
    const row = tabulator?.getRow(rowEl);
    if (!row) return;

    // Mutate the row data in place. `row.update()` would redraw the cell from
    // the stored HTML and drop the switch's live state — the toggle flashes
    // then snaps back (same constraint as remote_checkbox_toggle_controller).
    row.getData()[field] = cellEl.innerHTML;
  }
}
